import React from 'react';
import { Building2, FolderOpen, Folders, LucideIcon } from 'lucide-react';

import { BusinessUnitOption, ProjectOption } from '@/hooks/useProjectOptions';
import { cn } from '@/utils/utils';

/**
 * Shared visual language for scope levels, aligned with the rental parameter
 * scope icons (parameterScopeConfig): company = building, business unit =
 * folders, project = open folder.
 */
export type ProjectContextLevel = 'company' | 'business-unit' | 'project';

export const LEVEL_ICONS: Record<ProjectContextLevel, LucideIcon> = {
  company: Building2,
  'business-unit': Folders,
  project: FolderOpen,
};

export const LEVEL_LABELS: Record<ProjectContextLevel, string> = {
  company: 'Compañía',
  'business-unit': 'Unidad de negocio',
  project: 'Proyecto',
};

/**
 * Level tints, shared with the rental parameter scope badges. The blue →
 * cyan → green gradient mirrors the containment hierarchy, but the business
 * unit hue leans toward blue on purpose: the two folder icons need the
 * chromatic distance far more than the building does (a teal tried here
 * first collapsed into project green in dark mode). Color is applied
 * deliberately: only where rows of different levels sit side by side and
 * need telling apart (a user's role list, rental parameter sources).
 * Context pickers and switchers keep the icons monochrome.
 */
export const LEVEL_TINT_CLASSES: Record<ProjectContextLevel, string> = {
  company: 'text-blue-500 dark:text-blue-400',
  'business-unit': 'text-cyan-600 dark:text-cyan-400',
  project: 'text-green-500 dark:text-green-400',
};

export const projectContextLevel = (
  value: ProjectOption['value']
): ProjectContextLevel =>
  value.project_id != null
    ? 'project'
    : value.business_unit_id != null
      ? 'business-unit'
      : 'company';

export const ProjectContextIcon: React.FC<{
  level: ProjectContextLevel;
  className?: string;
}> = ({ level, className }) => {
  const Icon = LEVEL_ICONS[level];
  return (
    <div
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-sm border',
        className
      )}
    >
      <Icon className="size-4 shrink-0" />
    </div>
  );
};

/** One selector row: bordered level icon + label, optionally nested under its
 * business unit. */
export const ProjectContextRow: React.FC<{
  level: ProjectContextLevel;
  label: React.ReactNode;
  indented?: boolean;
  className?: string;
}> = ({ level, label, indented, className }) => (
  <div
    className={cn(
      'flex min-w-0 items-center gap-2',
      indented && 'pl-6',
      className
    )}
  >
    <ProjectContextIcon level={level} />
    <span className="truncate">{label}</span>
  </div>
);

/**
 * A business unit as a selectable `ProjectOption`: value carries
 * `business_unit_id` but no `project_id`. The label keeps the level explicit
 * because it is also what a combobox input shows once selected, where a bare
 * name would read as a project (BIL-4887).
 */
export const businessUnitProjectOption = (
  businessUnit: BusinessUnitOption,
  companyName: string
): ProjectOption => ({
  label: `Unidad de negocio: ${businessUnit.name}`,
  value: {
    company_id: businessUnit.company_id,
    business_unit_id: businessUnit.id,
  },
  names: { company: companyName, business_unit: businessUnit.name },
});

export type ProjectContextRowData = {
  option: ProjectOption;
  level: ProjectContextLevel;
  /** Combobox group heading this row belongs under, if any. */
  group?: string;
  /** Whether the row renders nested under its business unit. */
  indent: boolean;
};

/** What the row shows in a list: group headings and the level icon already
 * carry the level, so business units drop their label prefix. */
export const projectContextDisplayLabel = (row: ProjectContextRowData) =>
  row.level === 'business-unit'
    ? (row.option.names?.business_unit ?? row.option.label)
    : row.option.label;

/**
 * Whether a stored context value points at the same option. Values arrive
 * with different completeness (a form may store `{company_id, project_id}`
 * without the business unit), so each level compares only the ids that
 * define it.
 */
export const matchesProjectOptionValue = (
  a: ProjectOption['value'],
  b: ProjectOption['value']
): boolean => {
  if (a.company_id !== b.company_id) return false;
  if (a.project_id != null || b.project_id != null)
    return a.project_id === b.project_id;
  return a.business_unit_id === b.business_unit_id;
};

/**
 * Orders one company's context options the way the project switcher shows
 * them: company-wide options first, then each business unit followed by the
 * projects inside it, alphabetically.
 *
 * Without `companyGroup`, business units become group headings — but only
 * when they separate anything (more than one unit, or a selectable unit row
 * to nest under); a single-unit company keeps a flat list. With
 * `companyGroup` (multi-company lists), every row shares that heading and
 * nesting is conveyed by ordering + indentation alone.
 */
export const buildProjectContextRows = ({
  options,
  businessUnits,
  includeBusinessUnits = false,
  companyGroup,
}: {
  options: readonly ProjectOption[];
  businessUnits: readonly BusinessUnitOption[];
  /** Offer selectable business units as options themselves. */
  includeBusinessUnits?: boolean;
  companyGroup?: string;
}): ProjectContextRowData[] => {
  const companyLevel = options.filter(
    (option) =>
      option.value.project_id == null && option.value.business_unit_id == null
  );
  const projects = options
    .filter((option) => option.value.project_id != null)
    .toSorted((a, b) => a.label.localeCompare(b.label));
  // tests and legacy callers pass options without `names`, so every access to
  // it stays optional even though the type declares it
  const companyName = options[0]?.names?.company ?? '';
  const selectableBusinessUnits = includeBusinessUnits
    ? businessUnits.filter((businessUnit) => businessUnit.selectable)
    : [];

  const projectsByBusinessUnit = new Map<number | undefined, ProjectOption[]>();
  for (const project of projects) {
    const key = project.value.business_unit_id;
    projectsByBusinessUnit.set(key, [
      ...(projectsByBusinessUnit.get(key) ?? []),
      project,
    ]);
  }

  const businessUnitName = (id: number | undefined) =>
    businessUnits.find((businessUnit) => businessUnit.id === id)?.name ??
    projectsByBusinessUnit.get(id)?.[0]?.names?.business_unit ??
    '';

  const groupIds = [
    ...new Set([
      ...selectableBusinessUnits.map((businessUnit) => businessUnit.id),
      ...projectsByBusinessUnit.keys(),
    ]),
  ].toSorted((a, b) => businessUnitName(a).localeCompare(businessUnitName(b)));

  const useBusinessUnitHeadings =
    !companyGroup &&
    (groupIds.length > 1 || selectableBusinessUnits.length > 0);

  const rows: ProjectContextRowData[] = companyLevel.map((option) => ({
    option,
    level: 'company',
    group: companyGroup,
    indent: false,
  }));
  for (const id of groupIds) {
    const group =
      companyGroup ??
      (useBusinessUnitHeadings ? businessUnitName(id) : undefined);
    const selectable = selectableBusinessUnits.find(
      (businessUnit) => businessUnit.id === id
    );
    if (selectable) {
      rows.push({
        option: businessUnitProjectOption(selectable, companyName),
        level: 'business-unit',
        group,
        indent: false,
      });
    }
    // under a company heading there is no business-unit heading to nest
    // under, so only a business-unit row above earns the projects an indent
    const indent = companyGroup ? !!selectable : useBusinessUnitHeadings;
    for (const option of projectsByBusinessUnit.get(id) ?? []) {
      rows.push({ option, level: 'project', group, indent });
    }
  }
  return rows;
};
